
import React, { useState } from 'react';
import { AlertTriangle, RefreshCw, Trash2, CheckCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';

interface DiagnosticPanelProps {
  onReset: () => void;
}

const DiagnosticPanel: React.FC<DiagnosticPanelProps> = ({ onReset }) => {
  const [isRunningDiagnostics, setIsRunningDiagnostics] = useState(false);
  const [diagnosticResults, setDiagnosticResults] = useState<{
    memoryClean: boolean;
    localStorage: boolean;
  } | null>(null);

  const runDiagnostics = async () => {
    setIsRunningDiagnostics(true);
    
    try {
      // Check memory state
      const memoryClean = await checkMemoryState();
      
      // Check localStorage
      const localStorage = checkLocalStorage();
      
      setDiagnosticResults({
        memoryClean,
        localStorage
      });
      
      toast.success('Diagnostics completed');
    } catch (error) {
      toast.error('Diagnostics failed');
    } finally {
      setIsRunningDiagnostics(false);
    }
  };

  const checkMemoryState = async (): Promise<boolean> => {
    try {
      // Test if we can create clean objects
      const testData = { test: 'data', number: 123 };
      const channel = new MessageChannel();
      channel.port1.postMessage(testData);
      channel.port1.close();
      channel.port2.close();
      return true;
    } catch (error) {
      return false;
    }
  };

  const checkLocalStorage = (): boolean => {
    try {
      const testKey = 'diagnostic_test';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch (error) {
      return false;
    }
  };

  const clearAllData = () => {
    // Clear localStorage fallback data
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('campaign_local_')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    toast.success('All data cleared and system reset');
    
    // Re-run diagnostics
    setTimeout(runDiagnostics, 1000);
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-lg p-6">
      <div className="flex items-center mb-4">
        <AlertTriangle className="h-5 w-5 text-orange-500 mr-2" />
        <h3 className="text-lg font-medium">System Diagnostics</h3>
      </div>
      
      <div className="space-y-4">
        <button
          onClick={runDiagnostics}
          disabled={isRunningDiagnostics}
          className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {isRunningDiagnostics ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Running Diagnostics...
            </>
          ) : (
            <>
              <RefreshCw size={16} />
              Run Diagnostics
            </>
          )}
        </button>

        {diagnosticResults && (
          <div className="space-y-2 p-4 bg-neutral-50 rounded">
            <div className="flex items-center justify-between">
              <span>Memory State:</span>
              {diagnosticResults.memoryClean ? (
                <CheckCircle className="h-4 w-4 text-green-500" />
              ) : (
                <AlertTriangle className="h-4 w-4 text-red-500" />
              )}
            </div>
            <div className="flex items-center justify-between">
              <span>Local Storage:</span>
              {diagnosticResults.localStorage ? (
                <CheckCircle className="h-4 w-4 text-green-500" />
              ) : (
                <AlertTriangle className="h-4 w-4 text-red-500" />
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-2">
          <button
            onClick={clearAllData}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center justify-center gap-2"
          >
            <Trash2 size={16} />
            Clear All Data & Reset
          </button>
          
          <button
            onClick={onReset}
            className="px-4 py-2 bg-neutral-600 text-white rounded hover:bg-neutral-700 flex items-center justify-center gap-2"
          >
            <RefreshCw size={16} />
            Start Over
          </button>
        </div>
      </div>
    </div>
  );
};

export default DiagnosticPanel;
